import React from "react";
import { Card } from "./ui/card";
import { cn } from "~/lib/cn";
import {
  Calendar,
  Layout,
  Mail,
  MessageSquare,
  Monitor,
  PieChart,
  Users,
  X,
} from "lucide-react";
import { FeatureCheck } from "./ui/feature-check";

interface SoftwareSaving {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  examples: string[];
  monthlyCost: number;
  standardSaving: string | boolean;
  proSaving: string | boolean;
  proPlusSaving: string | boolean;
}

const softwareSavings: SoftwareSaving[] = [
  {
    name: "CRM Software",
    icon: PieChart,
    examples: ["HubSpot", "Pipedrive"],
    monthlyCost: 99,
    standardSaving: "50 contacts",
    proSaving: "5,000 contacts",
    proPlusSaving: "Unlimited",
  },
  {
    name: "Email Marketing Tool",
    icon: Mail,
    examples: ["MailChimp", "ActiveCampaign"],
    monthlyCost: 99,
    standardSaving: true,
    proSaving: true,
    proPlusSaving: true,
  },
  {
    name: "Online Course Platform",
    icon: Monitor,
    examples: ["Teachable", "Kajabi"],
    monthlyCost: 99,
    standardSaving: false,
    proSaving: true,
    proPlusSaving: true,
  },
  {
    name: "Landing Page Builder",
    icon: Layout,
    examples: ["ClickFunnels", "Leadpages"],
    monthlyCost: 99,
    standardSaving: false,
    proSaving: "1 Page",
    proPlusSaving: "Multiple Pages",
  },
  {
    name: "Automated Texting Tool",
    icon: MessageSquare,
    examples: ["Twilio", "EZ Texting"],
    monthlyCost: 27,
    standardSaving: false,
    proSaving: true,
    proPlusSaving: true,
  },
  {
    name: "Scheduling & Booking Software",
    icon: Calendar,
    examples: ["Calendly", "Acuity"],
    monthlyCost: 29,
    standardSaving: true,
    proSaving: true,
    proPlusSaving: true,
  },
  {
    name: "Community-Building Platform",
    icon: Users,
    examples: ["Mighty Networks", "Circle"],
    monthlyCost: 99,
    standardSaving: false,
    proSaving: false,
    proPlusSaving: true,
  },
];

const totalMonthlyCost = softwareSavings.reduce(
  (acc, curr) => acc + curr.monthlyCost,
  0,
);

interface SavingsComparisonTableProps {
  className?: string;
}

export const SavingsComparisonTable = ({
  className,
}: SavingsComparisonTableProps) => {
  return (
    <Card className={cn("overflow-hidden", className)}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-gray-50/50">
              <th className="text-text-secondary p-4 text-left font-medium">
                Software
              </th>
              <th className="text-text-secondary p-4 text-left font-medium">
                Monthly Cost
              </th>
              <th className="text-text-secondary p-4 text-left font-medium">
                Standard Plan
              </th>
              <th className="text-text-secondary p-4 text-left font-medium">
                Pro Plan
              </th>
              <th className="text-text-secondary p-4 text-left font-medium">
                Pro+ Plan
              </th>
            </tr>
          </thead>
          <tbody>
            {softwareSavings.map((software, index) => (
              <tr
                key={software.name}
                className={cn(
                  "border-b last:border-none",
                  1 === index % 2 && "bg-gray-50",
                )}
              >
                <td className="p-4">
                  <div className="flex gap-3">
                    <div className="pt-1">
                      <software.icon className="text-brand-700 h-8 w-8" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium">{software.name}</span>
                      <span className="text-text-tertiary text-sm">
                        e.g., {software.examples.join(", ")}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="p-4">${software.monthlyCost}</td>
                <td className="p-4">{renderSaving(software.standardSaving)}</td>
                <td className="p-4">{renderSaving(software.proSaving)}</td>
                <td className="p-4">{renderSaving(software.proPlusSaving)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-gray-200 bg-gray-50/50 font-medium">
              <td className="p-4">Total Monthly Cost</td>
              <td className="p-4">${totalMonthlyCost}</td>
              <td className="p-4">$130</td>
              <td className="p-4">$450</td>
              <td className="p-4">$550</td>
            </tr>
            <tr className="bg-gray-50/50 font-medium">
              <td className="p-4">Monthly Subscription</td>
              <td className="p-4">-</td>
              <td className="p-4">$0</td>
              <td className="p-4">$99</td>
              <td className="p-4">$159</td>
            </tr>
            <tr className="bg-purple-50 font-bold text-purple-900">
              <td className="p-4">Net Monthly Savings</td>
              <td className="p-4">-</td>
              <td className="p-4">$130</td>
              <td className="p-4">$350</td>
              <td className="p-4">$390</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

function renderSaving(saving: string | boolean) {
  if ("string" === typeof saving) {
    return saving;
  }
  return saving ? (
    <FeatureCheck />
  ) : (
    <div className="flex h-5 w-5 items-center justify-center">
      <X className="text-gray-300" />
    </div>
  );
}
